import React, { useMemo } from 'react';
import { Box, Button, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { MoreVert as MoreIcon, Repeat as RepeatIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme/tokens';
import { formatMoney } from '../../theme/chart';
import { parseLocalDate } from '../../utils/bills';
import { goalProgress } from '../../utils/goals';
import { Goal, GoalContribution } from '../../services/goalApi';
import { BigNumber, Tile } from '../dashboard/home/ui';
import { GROWTH_MARKS, eventPace, goalOutlook, monthsFromNow } from './goalOutlook';
import { balanceHistory, goalMonths } from './goalMonths';
import { GoalChart } from './GoalChart';
import { GoalMonthlyBars } from './GoalMonthlyBars';
import { GoalHistory } from './GoalHistory';
import { SegmentedBar } from './goalUi';

interface Props {
  goal: Goal;
  trail: GoalContribution[];
  now: Date;
  /** in a group, the history names who saved each amount */
  showPeople: boolean;
  canContribute: boolean;
  canAdjust: boolean;
  canModify: boolean;
  canUndo: (entry: GoalContribution) => boolean;
  onContribute: () => void;
  onAdjust: () => void;
  onMenu: (anchor: HTMLElement) => void;
  onUndo: (entry: GoalContribution) => void;
  /** the goal's name is the page's title on its own page */
  titleAs?: 'h1' | 'h3';
}

const DAY = 24 * 60 * 60 * 1000;

const Chip: React.FC<{ children: React.ReactNode; tone?: 'plain' | 'accent' }> = ({ children, tone = 'plain' }) => (
  <Box
    component='span'
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.6,
      px: 1.25,
      py: '3px',
      borderRadius: 99,
      bgcolor: tone === 'accent' ? tokens.accentSoft : tokens.bg,
      color: tone === 'accent' ? tokens.accentDeep : tokens.ink2,
      fontSize: 12.5,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </Box>
);

/** A month's name with a capital, for the start of a line: "Outubro". */
const capitalize = (text: string) => text.charAt(0).toLocaleUpperCase() + text.slice(1);

/**
 * One goal in full: what it is, how much is in it, the few numbers that say
 * whether it gets there, its path or growth, its deposits month by month and
 * its history. Everything a goal had in the old card, laid out as a page.
 */
export const GoalPanel: React.FC<Props> = ({
  goal,
  trail,
  now,
  showPeople,
  canContribute,
  canAdjust,
  canModify,
  canUndo,
  onContribute,
  onAdjust,
  onMenu,
  onUndo,
  titleAs = 'h3',
}) => {
  const { t, i18n } = useTranslation('planning');
  const locale = i18n.language;
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));

  const color = goal.color || tokens.accent;
  const isHabit = goal.goalType === 'recurring';
  const currency = goal.currency;
  const money = (value: number) => formatMoney(value, currency, locale);

  const outlook = useMemo(() => goalOutlook(goal, trail, now), [goal, trail, now]);
  const pace = useMemo(() => eventPace(goal, trail, now), [goal, trail, now]);
  const history = useMemo(() => balanceHistory(goal, trail, now), [goal, trail, now]);
  const columns = useMemo(() => goalMonths(goal, trail, outlook, now), [goal, trail, outlook, now]);

  const current = Math.max(0, Number(goal.currentAmount) || 0);
  const target = Number(goal.targetAmount ?? 0);
  const monthlyTarget = Number(goal.monthlyTarget ?? 0);
  const monthContributed = Number(goal.monthContributed ?? 0);
  const journey = goalProgress(current, target);
  const month = goalProgress(monthContributed, monthlyTarget);
  const monthName = now.toLocaleDateString(locale, { month: 'long' });
  const monthDone = isHabit && monthlyTarget > 0 && month.ratio >= 1;
  const deadline = goal.targetDate ? parseLocalDate(goal.targetDate) : null;
  const rate = Number(goal.expectedGrowthRate ?? 0);

  // The tiles: the few numbers that answer "do I get there?".
  const tiles: React.ReactNode[] = [];
  if (outlook?.kind === 'growth') {
    GROWTH_MARKS.forEach((mark, index) => {
      const grown = outlook.grown?.[mark];
      tiles.push(
        <Tile
          key={mark}
          label={t(mark === 12 ? 'goals.tiles.in1' : mark === 60 ? 'goals.tiles.in5' : 'goals.tiles.in10')}
          value={money(grown ?? outlook.flat[mark])}
          hint={grown != null ? t('goals.tiles.withoutYield', { amount: money(outlook.flat[mark]) }) : t('goals.tiles.depositsOnly')}
          tone={index === GROWTH_MARKS.length - 1 ? 'accent' : 'plain'}
          size={21}
        />,
      );
    });
  } else if (!isHabit && !goal.isAchieved && target > 0) {
    const arrival = pace.arrivalMonths != null ? monthsFromNow(pace.arrivalMonths, now) : null;
    const arrivalTile = (tone: 'plain' | 'accent' | 'warn', hint: string) => (
      <Tile
        key='arrival'
        label={t('goals.tiles.arrival')}
        value={arrival ? arrival.toLocaleDateString(locale, { month: 'short', year: 'numeric' }) : '—'}
        hint={arrival ? hint : t('goals.tiles.noArrival')}
        tone={arrival ? tone : 'plain'}
        size={21}
      />
    );

    if (outlook?.kind === 'path') {
      let arrivalHint = '';
      if (arrival && deadline) {
        const gap = (arrival.getFullYear() - deadline.getFullYear()) * 12 + (arrival.getMonth() - deadline.getMonth());
        arrivalHint = gap > 0
          ? t('goals.tiles.arrivalLate', { count: gap })
          : gap < 0
            ? t('goals.tiles.arrivalEarly', { count: -gap })
            : t('goals.tiles.arrivalOnTime');
      }
      const daysLeft = deadline ? Math.max((deadline.getTime() - now.getTime()) / DAY, 0) : 0;
      const remainingHint = daysLeft < 90
        ? t('goals.tiles.inWeeks', { count: Math.max(1, Math.round(daysLeft / 7)) })
        : t('goals.tiles.inMonths', { count: outlook.monthsToDeadline });
      tiles.push(
        <Tile key='needed' label={t('goals.tiles.needed')} value={money(outlook.needed)} hint={t('goals.tiles.neededHint')} size={21} />,
        <Tile key='pace' label={t('goals.tiles.pace')} value={money(outlook.average)} hint={t('goals.tiles.paceHint')} size={21} />,
        arrivalTile(outlook.behind ? 'warn' : 'accent', arrivalHint),
        <Tile key='remaining' label={t('goals.tiles.remaining')} value={money(pace.remaining)} hint={remainingHint} size={21} />,
      );
    } else {
      tiles.push(
        <Tile key='remaining' label={t('goals.tiles.remaining')} value={money(pace.remaining)} hint={deadline ? undefined : t('goals.tiles.noDeadline')} size={21} />,
        <Tile key='pace' label={t('goals.tiles.pace')} value={money(pace.average)} hint={t('goals.tiles.paceHint')} size={21} />,
        arrivalTile('plain', t('goals.tiles.atThisPace')),
      );
    }
  }

  const contribute = canContribute ? (
    <Button variant='contained' onClick={onContribute} sx={{ minHeight: 44, flexShrink: 0, ...(phone ? { width: '100%' } : {}) }}>
      {t('goals.contributeAction')}
    </Button>
  ) : null;

  return (
    <Box
      component='section'
      aria-label={goal.name}
      sx={{
        containerType: 'inline-size',
        minWidth: 0,
        bgcolor: tokens.surface,
        border: `1px solid ${tokens.line}`,
        borderRadius: `${tokens.radius.card}px`,
        p: { xs: 2.25, md: 3.5 },
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 2.75, md: 3.25 },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              <Chip>
                {isHabit && <RepeatIcon sx={{ fontSize: 14 }} />}
                {isHabit ? t('goals.habitChip') : t('goals.eventChip')}
              </Chip>
              {!isHabit && deadline && (
                <Chip>{t('goals.panel.until', { date: deadline.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: deadline.getFullYear() === now.getFullYear() ? undefined : 'numeric' }) })}</Chip>
              )}
              {rate > 0 && (
                <Chip>
                  {t(goal.growthRatePeriod === 'monthly' ? 'goals.panel.yieldMonthly' : 'goals.panel.yieldYearly', {
                    rate: rate.toLocaleString(locale, { maximumFractionDigits: 3 }),
                  })}
                </Chip>
              )}
              {goal.isAchieved && <Chip tone='accent'>{t('goals.achieved')}</Chip>}
              {monthDone && <Chip tone='accent'>{t('goals.monthComplete')}</Chip>}
            </Box>
            <Box component={titleAs} sx={{ m: 0, fontFamily: tokens.display, fontSize: { xs: 23, md: 28 }, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.15, overflowWrap: 'anywhere' }}>
              {goal.name}
            </Box>
          </Box>
          {!phone && contribute}
          {canModify && (
            <IconButton aria-label={t('goals.rowMenu') as string} onClick={(event) => onMenu(event.currentTarget)} sx={{ color: tokens.ink3, mr: -1 }}>
              <MoreIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1.25, rowGap: 0.5 }}>
          <BigNumber size={52}>{money(current)}</BigNumber>
          <Box component='span' sx={{ fontSize: 17, color: tokens.ink3 }}>
            {target > 0 ? t('goals.panel.of', { target: money(target) }) : t('goals.panel.accumulated')}
          </Box>
        </Box>

        {isHabit ? (
          monthlyTarget > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontSize: 13.5 }}>
                <span>{capitalize(monthName)}</span>
                <span>
                  <strong>{money(monthContributed)}</strong>{' '}
                  <Box component='span' sx={{ color: tokens.ink3 }}>{t('goals.panel.of', { target: money(monthlyTarget) })}</Box>
                </span>
              </Box>
              <SegmentedBar
                ratio={month.ratio}
                color={color}
                label={t('goals.panel.monthShare', { percent: Math.round(month.ratio * 100), month: monthName })}
              />
            </Box>
          )
        ) : (
          target > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <SegmentedBar ratio={journey.ratio} color={color} label={t('goals.panel.saved', { percent: Math.round(journey.ratio * 100) })} />
              <Box aria-hidden sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', fontSize: 12, color: tokens.ink3 }}>
                <span>25%</span>
                <span>50%</span>
                <span>75%</span>
                <Box component='span' sx={{ textAlign: 'right' }}>{money(target)}</Box>
              </Box>
            </Box>
          )
        )}

        {phone && contribute}
      </Box>

      {tiles.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            [`@container (min-width: ${tiles.length > 3 ? 620 : 480}px)`]: { gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))` },
          }}
        >
          {tiles}
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 3.5,
          gridTemplateColumns: 'minmax(0, 1fr)',
          alignItems: 'start',
          '@container (min-width: 700px)': { gridTemplateColumns: 'minmax(0, 1.7fr) minmax(0, 1fr)' },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5, minWidth: 0 }}>
          <GoalChart
            goal={goal}
            outlook={outlook}
            history={history}
            average={pace.average}
            arrivalMonths={pace.arrivalMonths}
            color={color}
            currency={currency}
            now={now}
          />
          <GoalMonthlyBars
            goal={goal}
            columns={columns}
            outlook={outlook}
            average={isHabit ? undefined : pace.average}
            arrivalMonths={isHabit ? undefined : pace.arrivalMonths}
            color={color}
            currency={currency}
            now={now}
          />
        </Box>
        <GoalHistory
          trail={trail}
          color={color}
          currency={currency}
          showPeople={showPeople}
          canAdjust={canAdjust}
          onAdjust={onAdjust}
          canUndo={canUndo}
          onUndo={onUndo}
        />
      </Box>
    </Box>
  );
};
